import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {Box, Button, Menu, MenuItem} from '@mui/material'
import type {FC, ReactElement} from 'react'
import {useMemo, useState} from 'react'
import {getFormattedDate} from '@app/util-functions'
import {useUserContext} from '@containers/App/UserProvider'
import type {UserContextProps} from '@containers/App/UserProvider'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import SingleDateTimeSelector from '@shared/components/timerange/SingleDateTimeSelector'

export type TimeRange = {start: string; end: string}

/**
 * When an entry happened.
 *
 * - now: a single moment, taken when the entry is saved.
 * - batch: the batch (or state segment) the user clicked on the timeline.
 * - custom: a start and end the user picked.
 */
export type TimeChoice =
  | {kind: 'now'}
  | {kind: 'batch'; range: TimeRange}
  | {kind: 'custom'; range: TimeRange}

const ms = (iso: string): number => new Date(iso).getTime()

/** The entry's start and end for a choice, `now` resolved at call time. */
export const resolveTimeChoice = (choice: TimeChoice): TimeRange => {
  if (choice.kind === 'now') {
    const now = new Date().toISOString()
    return {start: now, end: now}
  }
  return choice.range
}

interface EntryTimeChipProps {
  value: TimeChoice
  onChange: (choice: TimeChoice) => void
  /** The clicked batch, offered as an option when there is one. */
  batchRange: TimeRange | null
}

const EntryTimeChip: FC<EntryTimeChipProps> = ({value, onChange, batchRange}): ReactElement => {
  const [t] = useCustomTranslation('logbook')
  const [tDashboard] = useCustomTranslation('dashboards')
  const {currentUser} = useUserContext() as UserContextProps
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const time = (iso: string): string =>
    getFormattedDate(iso, currentUser, {showDate: false, showTime: true})
  const dateTime = (iso: string): string =>
    getFormattedDate(iso, currentUser, {showDate: true, showTime: true})
  const span = (range: TimeRange): string => {
    const sameDay =
      getFormattedDate(range.start, currentUser, {}) ===
      getFormattedDate(range.end, currentUser, {})
    if (range.start === range.end) {
      return dateTime(range.start)
    }
    return sameDay
      ? `${dateTime(range.start)}–${time(range.end)}`
      : `${dateTime(range.start)} – ${dateTime(range.end)}`
  }

  const label =
    value.kind === 'now'
      ? t('time.now')
      : value.kind === 'batch'
        ? `${t('time.batch')} · ${span(value.range)}`
        : span(value.range)

  const choose = (choice: TimeChoice): void => {
    onChange(choice)
    setAnchor(null)
  }

  const customRange: TimeRange =
    value.kind === 'now'
      ? {start: new Date().toISOString(), end: new Date().toISOString()}
      : value.range

  const setCustom = (date: Date | null, selector: string): void => {
    if (!date || Number.isNaN(date.getTime())) {
      return
    }
    const iso = date.toISOString()
    onChange({
      kind: 'custom',
      range:
        selector === 'logbook-entry-start'
          ? {start: iso, end: ms(customRange.end) < date.getTime() ? iso : customRange.end}
          : {start: customRange.start, end: iso},
    })
  }

  const isInvalid = value.kind === 'custom' && ms(value.range.start) > ms(value.range.end)

  // The picker gets the same Date object until the time really changes: its
  // calendar jumps back to the selected month whenever the value object is
  // new, so a fresh Date per render undid every click on the month arrows.
  const customStart = value.kind === 'custom' ? value.range.start : null
  const customEnd = value.kind === 'custom' ? value.range.end : null
  const startDate = useMemo(() => (customStart ? new Date(customStart) : null), [customStart])
  const endDate = useMemo(() => (customEnd ? new Date(customEnd) : null), [customEnd])

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<AccessTimeIcon fontSize="small" />}
        endIcon={<ArrowDropDownIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{textTransform: 'none', color: 'text.secondary', fontWeight: 400, px: 0.5}}
        data-cy="logbook-entry-time"
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <MenuItem selected={value.kind === 'now'} onClick={() => choose({kind: 'now'})}>
          {t('time.now')}
        </MenuItem>
        {batchRange && (
          <MenuItem
            selected={value.kind === 'batch'}
            onClick={() => choose({kind: 'batch', range: batchRange})}
          >
            {`${t('time.batch')} · ${span(batchRange)}`}
          </MenuItem>
        )}
        <MenuItem
          selected={value.kind === 'custom'}
          onClick={() => choose({kind: 'custom', range: customRange})}
          data-cy="logbook-entry-time-custom"
        >
          {t('time.custom')}
        </MenuItem>
      </Menu>
      {value.kind === 'custom' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            flexBasis: '100%',
            // Below the whole row, after the Save button (see EntryComposer).
            order: 3,
            mt: 1,
            // Stacked and full width: side by side, the two pickers overflow the panel.
            '& .MuiFormControl-root': {width: '100%'},
          }}
        >
          <SingleDateTimeSelector
            id="logbook-entry-start"
            label={tDashboard('start_time')}
            timeSelector="start"
            onDateChange={setCustom}
            onFocusChanged={() => {}}
            defaultValue={startDate}
            error={isInvalid}
          />
          <SingleDateTimeSelector
            id="logbook-entry-end"
            label={tDashboard('end_time')}
            timeSelector="end"
            onDateChange={setCustom}
            onFocusChanged={() => {}}
            defaultValue={endDate}
            error={isInvalid}
            helperText={isInvalid ? t('errors.wrong_time_interval') : undefined}
          />
        </Box>
      )}
    </>
  )
}

export default EntryTimeChip
